import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Sun, Cloud, Wind, Droplets } from 'lucide-react';
import { weatherData } from '../mock';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const WeatherCard = ({ item }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#0a3d62' }}>
        {item.name}
      </CardTitle>
    </CardHeader>
    <CardContent className="grid grid-cols-2 gap-4 text-sm">
      <div className="flex items-center gap-2">
        <Sun className="w-5 h-5 text-gray-500" />
        <div>
          <p className="text-gray-500">Temperature</p>
          <p className="font-semibold">{item.temperature}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Cloud className="w-5 h-5 text-gray-500" />
        <div>
          <p className="text-gray-500">Condition</p>
          <p className="font-semibold">{item.condition}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Wind className="w-5 h-5 text-gray-500" />
        <div>
          <p className="text-gray-500">Wind</p>
          <p className="font-semibold">{item.wind}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Droplets className="w-5 h-5 text-gray-500" />
        <div>
          <p className="text-gray-500">Humidity</p>
          <p className="font-semibold">{item.humidity}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 col-span-2">
        <Sun className="w-5 h-5 text-gray-500" />
        <div>
          <p className="text-gray-500">Irradiance (GHI)</p>
          <p className="font-semibold">{item.irradiance}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const Weather = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ color: '#0a3d62' }}>Weather Overview</h1>
        <p className="text-base mt-2" style={{ color: '#576574' }}>Real-time weather data for sites and districts.</p>
      </div>

      {/* Weather Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Temperature Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Line
                data={{
                  labels: weatherData.sites.map(site => site.name),
                  datasets: [{
                    label: 'Temperature (°C)',
                    data: weatherData.sites.map(site => parseInt(site.temperature)),
                    borderColor: '#e74c3c',
                    backgroundColor: 'rgba(231, 76, 60, 0.1)',
                    tension: 0.4,
                    fill: true,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    },
                    x: {
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Irradiance Levels</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar
                data={{
                  labels: weatherData.sites.map(site => site.name),
                  datasets: [{
                    label: 'Irradiance (W/m²)',
                    data: weatherData.sites.map(site => parseInt(site.irradiance)),
                    backgroundColor: 'rgba(243, 156, 18, 0.8)',
                    borderColor: '#f39c12',
                    borderWidth: 1,
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      titleColor: '#fff',
                      bodyColor: '#fff',
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    },
                    x: {
                      grid: {
                        color: 'rgba(0, 0, 0, 0.1)',
                      },
                      ticks: {
                        color: '#576574',
                      }
                    }
                  }
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="site">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="site">By Site</TabsTrigger>
          <TabsTrigger value="district">By District</TabsTrigger>
        </TabsList>
        <TabsContent value="site">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            {weatherData.sites.map(site => (
              <WeatherCard key={site.id} item={site} />
            ))}
          </div>
        </TabsContent>
        <TabsContent value="district">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            {weatherData.districts.map(district => (
              <WeatherCard key={district.id} item={district} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Weather;
